import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslate } from '../../../mixins/translate/translate';
import { openApiFetchGet } from '../../../store/openApiFetch';
import { EditPanel, EditPanelSection, PanelBody, PanelHeading, PanelWithoutForm } from '../../elements';

interface IVideoStreamProps {
    surveyId: string;
}

function RetryingVideo(props: { sourceUrl: string }) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const checkInterval = useRef<NodeJS.Timeout | null>(null);

    const loadVideo = () => {
        const video = videoRef.current;
        if (video && video.networkState === video.NETWORK_NO_SOURCE) {
            // NETWORK_NO_SOURCE: no information whether video is available details.
            video.load();
        } else if (video && video.networkState === video.NETWORK_LOADING) {
            // NETWORK_LOADING: browser is downloading data.
            if (checkInterval.current) {
                clearInterval(checkInterval.current);
            }
        } else if (video) {
            try {
                void video.play();
            } catch {}
        }
    };

    useEffect(() => {
        checkInterval.current = setInterval(loadVideo, 5000); // Retry every 5 seconds.

        return () => {
            if (checkInterval.current) {
                clearInterval(checkInterval.current); // Clean up interval on unmount.
            }
        };
    }, []);

    return (
        <video ref={videoRef} crossOrigin="anonymous" style={{ width: '100%' }} autoPlay controls>
            <source src={props.sourceUrl} type="video/mp4" />
        </video>
    );
}
export function VideoStream(props: IVideoStreamProps) {
    const { surveyId } = props;
    const translate = useTranslate();
    const [state, setState] = React.useState<{ isLoading: boolean; rtmpUrl: string | null; hasServerError: boolean; videoUrl: string | null }>({
        hasServerError: false,
        isLoading: true,
        rtmpUrl: null,
        videoUrl: null,
    });

    const load = useCallback(async () => {
        try {
            const response = await openApiFetchGet('/api/surveys/{surveyId}/videoStream', { surveyId });
            setState({ isLoading: false, hasServerError: false, rtmpUrl: response.rtmpUrl, videoUrl: response.videoUrl });
        } catch (error) {
            if ('statusCode' in error && error.statusCode === 404) {
                setState({ isLoading: false, hasServerError: false, rtmpUrl: null, videoUrl: null });
            } else {
                setState({ isLoading: false, hasServerError: true, rtmpUrl: null, videoUrl: null });
            }
        }
    }, [surveyId]);

    useEffect(() => {
        void load();
    }, [load]);

    if (!state.isLoading && state.rtmpUrl && state.videoUrl) {
        return (
            <PanelWithoutForm id="Stream">
                <PanelHeading text={translate('videoStream.videoStream')} icon="fa fa-video-camera" />
                <EditPanel bottomMargin={true} noPadding={true}>
                    <PanelBody isForm={true}>
                        <EditPanelSection>
                            <RetryingVideo sourceUrl={state.videoUrl} />
                        </EditPanelSection>
                    </PanelBody>
                </EditPanel>
            </PanelWithoutForm>
        );
    }
    return null;
}
